import React, { useCallback, useMemo, useRef } from "react";
import { EReportTypes, REPORT_TYPES } from "../routes/report_types";
import { BASE_ZINDEX } from "../../../popup-factory";
import { Button, colors, Csku, Popup, SkubotSpinner } from "@commonsku/styles";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { closePopup } from "../../../actions/popup";
import { getReportConfig, ReportConfig, ReportElementSizes } from "../config";
import { ReportProvider, useReportContext } from "../report_context";
import ReportTable from "../table/ReportTable";
import { ScrollContainer } from "../../../hooks/useContainerScroll";
import { useContainerScroll } from "../../../hooks";
import { PopupHeaderWithCloseIcon } from "../../FormPopup";

const StyledBottomButton = styled("div")`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: flex-end;
  position: absolute;
  right: 16px;
  bottom: 16px;
`;

const LoadReportContainer = styled.div`
  width: 100%;
  height: calc(100% - 40px);
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledEmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 40px);
`;

const STYLE_CONSTANTS: ReportElementSizes = {
  SIDE_BAR_WIDTH: 192,
  PREFERRED_WINDOW_WIDTH: 1512,
  TABLE_ELEMENTS_GAP: 16,
  SUMMARY_CONTAINER_HEIGHT: 0,
  TABLE_PAGE_HEADER_HEIGHT: 0,
  ROW_HEIGHT: 56,
  TABLE_BOTTOM_MARGIN: 80,
  BULK_ACTION_BAR_HEIGHT: 0,
};

interface LoadReportPopupProps {
  onClose?: () => void;
  report_type?: string;
}

const EmptyState = () => (
  <StyledEmptyStateContainer>
    <SkubotSpinner />
  </StyledEmptyStateContainer>
);

const LoadReportPopup = ({ onClose, report_type }: LoadReportPopupProps) => {
  const dispatch = useDispatch();
  const onPopupClose = () => (onClose ? onClose() : dispatch(closePopup()));

  const SavedReportConfig: ReportConfig = useMemo(
    () => getReportConfig(EReportTypes.SavedReports),
    [],
  );

  const defaultFilters = useMemo(
    () => ({ "max-results": 9999, ...(report_type ? { report_type } : null) }),
    [],
  );

  const ReportContentWithScrollers = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { rowData, firstLoaded } = useReportContext();

    const filterRows = useCallback((row) => {
      return !!REPORT_TYPES[row["report_type"]["report_type_name"]];
    }, []);

    const filteredRowData = useMemo(() => {
      return rowData.filter(filterRows);
    }, [rowData, filterRows]);

    const { scrollUp, scrollDown, canScrollUp, canScrollDown } =
      useContainerScroll(containerRef, [filteredRowData.length, firstLoaded]);

    const renderScrollContainers = useCallback(() => {
      if (!containerRef.current) {
        return null;
      }

      const { left, top, width, height, bottom } =
        containerRef.current.getBoundingClientRect();

      const scrollContainerProps = (isUp: boolean = true) => ({
        isUp,
        onClick: isUp ? scrollUp : scrollDown,
        canScroll: isUp ? canScrollUp() : canScrollDown(),
        color: colors.teal.main,
        width: width + 32,
        zIndex: 99999,
        top: isUp ? top + 40 : "auto",
        bottom: isUp ? "auto" : bottom - height - 26,
        left: left - 20,
        right: "auto",
        style: { cursor: "pointer" },
      });

      return (
        <>
          <ScrollContainer {...scrollContainerProps()} />
          <ScrollContainer {...scrollContainerProps(false)} />
        </>
      );
    }, [canScrollDown, canScrollUp, scrollDown, scrollUp, containerRef]);

    return (
      <>
        <LoadReportContainer ref={containerRef}>
          <ReportTable
            wrapperClassName="saved-report-table-wrapper"
            headerClassName="saved-report-table-header"
            EmptyStateComponent={EmptyState}
            extraRowFilter={filterRows}
          />
          {renderScrollContainers()}
        </LoadReportContainer>
      </>
    );
  };

  return (
    <Popup
      closeOnEsc={false}
      closeOnClickOutside={false}
      style={{ width: "70vw", height: 600 }}
      overlayZIndex={BASE_ZINDEX + 100}
      zIndex={BASE_ZINDEX + 200}
      header={
        <PopupHeaderWithCloseIcon
          title="Load Report"
          onPopupClose={onPopupClose}
        />
      }
      onClose={onPopupClose}
    >
      <ReportProvider
        defaultFilters={defaultFilters}
        reportConfig={SavedReportConfig}
        elementSizeParameters={STYLE_CONSTANTS}
      >
        <ReportContentWithScrollers />
      </ReportProvider>
      <StyledBottomButton>
        <Button
          style={{ width: 139, height: 40, padding: 0 }}
          onClick={onPopupClose}
        >
          Close
        </Button>
      </StyledBottomButton>
    </Popup>
  );
};

export default LoadReportPopup;
